import React, {createContext, ReactElement, useCallback, useContext, useEffect, useState,} from 'react';
import {AppUser, LoginResponse} from '../scenes/unauthenticated/types';
import {userLogin, userSignout} from '../scenes/unauthenticated/server';
import {FetchWithJsonOrTextReponseWithError} from '../fetcher';
import eventBus from '../events/eventBus';
import i18n from 'i18next';
import {TokenPayload} from "../types";
import {LocalAuthentication} from "../scenes/authenticated/types";

interface AuthContextType {
    user: AppUser | null;
    login: (body: string) =>  Promise<FetchWithJsonOrTextReponseWithError<LoginResponse, any>>;
    signOut: () => void;
    isAuthenticated: () => boolean;
    verifyAuthentication: () => void;
    verifyAuthenticationOnFirstLoad: () => void;
}

export const AuthContext = createContext<AuthContextType>(
    {} as AuthContextType,
);

export const useAuth = () => useContext(AuthContext);

const base64 = require('base-64');

interface Props {
    children: ReactElement;
}

export default function AuthenticationContext({children}: Props): ReactElement {

    const JWT_TOKEN_KEY = 'JWT_TOKEN_2';
    const APP_USER_KEY = 'APP_USER_2';

    function getTokenExpirationTime(token: string) {
        const tokenParts = token.split('.');
        if (tokenParts.length >= 2) {
            const payload = tokenParts[1];
            const decodedPayload = JSON.parse(base64.decode(payload)) as TokenPayload;
            return decodedPayload.exp * 1000;
        }
        return null;
    }

    const getLocalAuthentication = (user: string | null, token: string | null): LocalAuthentication | null => {
        if (token && user) {
            let expirationTimeUnix = getTokenExpirationTime(token);
            if (expirationTimeUnix && expirationTimeUnix > Date.now()) {
                const localAuth = JSON.parse(user);
                return {
                    appUser: localAuth.appUser,
                    tokenExpirationTime: expirationTimeUnix,
                };
            }
        }
        return null;
    };

    const [localAuthentication, setLocalAuthentication] = useState<LocalAuthentication | null>(() => getLocalAuthentication(
        localStorage.getItem(APP_USER_KEY),
        localStorage.getItem(JWT_TOKEN_KEY)
    ));

    useEffect(() => {
        // @ts-ignore
        window.Intercom('shutdown');

        const intercomUrl = 'https://api-iam.intercom.io';
        const intercomAppId = 'strh6iag';

        if (localAuthentication) {
            localStorage.setItem(APP_USER_KEY, JSON.stringify(localAuthentication));
            // @ts-ignore
            window.Intercom('boot', {
                api_base: intercomUrl,
                app_id: intercomAppId,
                name: localAuthentication.appUser.name,
                email: localAuthentication.appUser.email,
                created_at: localAuthentication.appUser.createdAt,
                language_override: i18n.language,
            });
        } else {
            localStorage.removeItem(JWT_TOKEN_KEY);
            localStorage.removeItem(APP_USER_KEY);
            // @ts-ignore
            window.Intercom('boot', {
                api_base: intercomUrl,
                app_id: intercomAppId,
                language_override: i18n.language,
            });
        }
    }, [localAuthentication]);

    useEffect(() => {
        eventBus.on('clearUser', () => setLocalAuthentication(null));
        return () => eventBus.remove('clearUser');
    }, []);

    const verifyAuthentication = () => {
        if (!isAuthenticated()) {
            setLocalAuthentication(null);
        }
    };

    const verifyAuthenticationOnFirstLoad = () => {
        if (!isAuthenticated()) {
            localStorage.removeItem(JWT_TOKEN_KEY);
            localStorage.removeItem(APP_USER_KEY);
        }
    };

    const isAuthenticated = () => {
        const expirationTime = localAuthentication?.tokenExpirationTime;
        if (expirationTime) {
            return expirationTime > Date.now();
        } else {
            return false;
        }
    };

    const login = (body: string) => {
        return userLogin(body)
            .then((r) => {
                if (r.status === 'SUCCESS' && r.dataType === 'json' &&r.data) {
                    const tokenExpirationTime = getTokenExpirationTime(r.data.accessToken);
                    if (tokenExpirationTime) {
                        setLocalAuthentication({appUser: r.data.appUser, tokenExpirationTime: tokenExpirationTime});
                        localStorage.setItem(JWT_TOKEN_KEY, r.data.accessToken);
                        return Promise.resolve(r);
                    }
                }
                return Promise.reject(r);
            })
            .catch((e) => {
                return Promise.reject(e);
            });
    };

    const signOut = useCallback(async () => {
        const userString = localStorage.getItem(APP_USER_KEY)!!;
        const token = localStorage.getItem(JWT_TOKEN_KEY);
        const localAuth = JSON.parse(userString) as LocalAuthentication;
        const user = localAuth.appUser;

        userSignout(JSON.stringify({ email: user.email, token: token })).finally(() => setLocalAuthentication(null));
    }, [setLocalAuthentication]);

    const user = localAuthentication ? localAuthentication.appUser : null;

    return (
        <AuthContext.Provider value={{ user: user, login,  signOut, isAuthenticated, verifyAuthentication, verifyAuthenticationOnFirstLoad }}>
            {children}
        </AuthContext.Provider>
    );
}
